import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from '@/App';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Skeleton } from '@/components/ui/skeleton';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { 
  Shield, 
  ArrowLeft,
  Eye,
  EyeOff,
  MessageSquare,
  AlertTriangle,
  Trash2,
  Power,
  Loader2,
  Save
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Privacy = () => {
  const { language } = useLanguage();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [hasProfile, setHasProfile] = useState(false);
  const [panicDialog, setPanicDialog] = useState(false);
  const [deleteDialog, setDeleteDialog] = useState(false);
  const [panicLoading, setPanicLoading] = useState(false);
  
  const [settings, setSettings] = useState({
    profile_visibility: 'pseudonymous',
    discoverable: true,
    show_in_group_directories: true,
    who_can_contact: 'everyone',
    contact_preference: 'in_app',
    show_activity_status: false,
    show_profile_views: false,
    leave_view_traces: false,
    panic_mode_enabled: false
  });

  useEffect(() => {
    checkProfileAndLoadSettings();
  }, []);

  const checkProfileAndLoadSettings = async () => {
    try {
      const token = localStorage.getItem('polibot_token');
      
      // Check if user has community profile
      const checkRes = await axios.get(`${API}/community/profile/check/exists`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setHasProfile(checkRes.data.has_profile);
      
      if (checkRes.data.has_profile) {
        // Load privacy settings
        try {
          const settingsRes = await axios.get(`${API}/community/privacy-settings`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          if (settingsRes.data.settings) {
            setSettings(settingsRes.data.settings);
          }
        } catch (e) {
          // Settings might not exist yet, use defaults
        }
      }
    } catch (error) {
      console.error('Error loading settings:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const token = localStorage.getItem('polibot_token');
      await axios.put(`${API}/community/privacy-settings`, settings, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(language === 'es' ? 'Configuración guardada' : 'Settings saved');
    } catch (error) {
      toast.error(language === 'es' ? 'Error al guardar' : 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const handlePanicMode = async () => {
    setPanicLoading(true);
    try {
      const token = localStorage.getItem('polibot_token');
      await axios.post(`${API}/community/safety/panic`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(language === 'es' 
        ? 'Modo de pánico activado. Tu perfil está oculto.' 
        : 'Panic mode activated. Your profile is hidden.');
      setPanicDialog(false);
      navigate('/community');
    } catch (error) {
      toast.error(language === 'es' ? 'Error' : 'Failed');
    } finally {
      setPanicLoading(false);
    }
  };

  const handleDeleteProfile = async () => {
    try {
      const token = localStorage.getItem('polibot_token');
      await axios.delete(`${API}/community/data/delete`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(language === 'es' 
        ? 'Perfil comunitario eliminado' 
        : 'Community profile deleted');
      setDeleteDialog(false);
      navigate('/community');
    } catch (error) {
      toast.error(language === 'es' ? 'Error' : 'Failed');
    }
  };

  if (loading) {
    return (
      <div className="max-w-2xl mx-auto space-y-6" data-testid="privacy-loading">
        <Skeleton className="h-10 w-64" />
        <Skeleton className="h-64" />
        <Skeleton className="h-64" />
      </div>
    );
  }

  if (!hasProfile) {
    return (
      <div className="max-w-2xl mx-auto space-y-6 animate-fade-in" data-testid="no-profile">
        <Button variant="ghost" onClick={() => navigate('/settings')}>
          <ArrowLeft className="mr-2 h-4 w-4" />
          {language === 'es' ? 'Atrás' : 'Back'}
        </Button>
        
        <Card>
          <CardContent className="pt-6 text-center">
            <Shield className="h-12 w-12 mx-auto mb-4 text-muted-foreground opacity-50" />
            <h2 className="text-xl font-semibold mb-2">
              {language === 'es' ? 'Sin Perfil Comunitario' : 'No Community Profile'}
            </h2>
            <p className="text-muted-foreground mb-4">
              {language === 'es' 
                ? 'Crea un perfil comunitario para acceder a la configuración de privacidad'
                : 'Create a community profile to access privacy settings'}
            </p>
            <Button onClick={() => navigate('/community/onboarding')}>
              {language === 'es' ? 'Crear Perfil' : 'Create Profile'}
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="max-w-2xl mx-auto space-y-6 animate-fade-in" data-testid="privacy-settings">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Button variant="ghost" size="icon" onClick={() => navigate('/settings')}>
          <ArrowLeft className="h-5 w-5" />
        </Button>
        <div>
          <h1 className="text-2xl md:text-3xl font-bold tracking-tight flex items-center gap-2">
            <Shield className="h-6 w-6 text-primary" />
            {language === 'es' ? 'Configuración de Privacidad' : 'Privacy Settings'}
          </h1>
          <p className="text-muted-foreground mt-1">
            {language === 'es' 
              ? 'Controla tu visibilidad y datos en la comunidad'
              : 'Control your visibility and data in the community'}
          </p>
        </div>
      </div>

      {/* Visibility Settings */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Eye className="h-5 w-5" />
            {language === 'es' ? 'Visibilidad' : 'Visibility'}
          </CardTitle>
          <CardDescription>
            {language === 'es' 
              ? 'Controla cómo otros te ven'
              : 'Control how others see you'}
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="space-y-3">
            <Label>{language === 'es' ? 'Nivel de Perfil' : 'Profile Level'}</Label>
            <RadioGroup 
              value={settings.profile_visibility} 
              onValueChange={(v) => setSettings(prev => ({ ...prev, profile_visibility: v }))}
            >
              <div className="flex items-start space-x-3 p-3 rounded-lg border">
                <RadioGroupItem value="anonymous" id="anonymous" />
                <div className="flex-1">
                  <Label htmlFor="anonymous" className="flex items-center gap-2 cursor-pointer">
                    <EyeOff className="h-4 w-4" />
                    {language === 'es' ? 'Anónimo' : 'Anonymous'}
                  </Label>
                  <p className="text-sm text-muted-foreground">
                    {language === 'es' ? 'Solo áreas de interés visibles' : 'Only issue areas visible'}
                  </p>
                </div>
              </div>
              <div className="flex items-start space-x-3 p-3 rounded-lg border">
                <RadioGroupItem value="pseudonymous" id="pseudonymous" />
                <div className="flex-1">
                  <Label htmlFor="pseudonymous" className="flex items-center gap-2 cursor-pointer">
                    {language === 'es' ? 'Pseudónimo' : 'Pseudonymous'}
                  </Label>
                  <p className="text-sm text-muted-foreground">
                    {language === 'es' ? 'Nombre de usuario + ciudad' : 'Display name + city'}
                  </p>
                </div>
              </div>
              <div className="flex items-start space-x-3 p-3 rounded-lg border">
                <RadioGroupItem value="organization" id="organization" />
                <div className="flex-1">
                  <Label htmlFor="organization" className="flex items-center gap-2 cursor-pointer">
                    {language === 'es' ? 'Organización' : 'Organization'}
                  </Label>
                  <p className="text-sm text-muted-foreground">
                    {language === 'es' ? 'Perfil público completo' : 'Full public profile'}
                  </p>
                </div>
              </div>
            </RadioGroup>
          </div>

          <div className="flex items-center justify-between py-3 border-t">
            <div>
              <Label>{language === 'es' ? 'Ser Descubrible' : 'Be Discoverable'}</Label>
              <p className="text-sm text-muted-foreground">
                {language === 'es' ? 'Aparecer en búsquedas' : 'Appear in searches'}
              </p>
            </div>
            <Switch
              checked={settings.discoverable}
              onCheckedChange={(checked) => setSettings(prev => ({ ...prev, discoverable: checked }))}
              data-testid="discoverable-switch"
            />
          </div>

          <div className="flex items-center justify-between py-3 border-t">
            <div>
              <Label>{language === 'es' ? 'Mostrar en Directorios de Grupo' : 'Show in Group Directories'}</Label>
              <p className="text-sm text-muted-foreground">
                {language === 'es' ? 'Visible en listas de miembros' : 'Visible in member lists'}
              </p>
            </div>
            <Switch
              checked={settings.show_in_group_directories}
              onCheckedChange={(checked) => setSettings(prev => ({ ...prev, show_in_group_directories: checked }))}
            />
          </div>
        </CardContent>
      </Card>

      {/* Contact Settings */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <MessageSquare className="h-5 w-5" />
            {language === 'es' ? 'Contacto' : 'Contact'}
          </CardTitle>
          <CardDescription>
            {language === 'es' 
              ? 'Controla quién puede contactarte'
              : 'Control who can contact you'}
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="space-y-3">
            <Label>{language === 'es' ? '¿Quién puede contactarte?' : 'Who can contact you?'}</Label>
            <RadioGroup 
              value={settings.who_can_contact} 
              onValueChange={(v) => setSettings(prev => ({ ...prev, who_can_contact: v }))}
            >
              <div className="flex items-center space-x-3">
                <RadioGroupItem value="everyone" id="everyone" />
                <Label htmlFor="everyone">{language === 'es' ? 'Todos' : 'Everyone'}</Label>
              </div>
              <div className="flex items-center space-x-3">
                <RadioGroupItem value="verified_only" id="verified_only" />
                <Label htmlFor="verified_only">{language === 'es' ? 'Solo Verificados' : 'Verified Only'}</Label>
              </div>
              <div className="flex items-center space-x-3">
                <RadioGroupItem value="connections_only" id="connections_only" />
                <Label htmlFor="connections_only">{language === 'es' ? 'Solo Conexiones' : 'Connections Only'}</Label>
              </div>
              <div className="flex items-center space-x-3">
                <RadioGroupItem value="nobody" id="nobody" />
                <Label htmlFor="nobody">{language === 'es' ? 'Nadie' : 'Nobody'}</Label>
              </div>
            </RadioGroup>
          </div>

          <div className="space-y-3 pt-3 border-t">
            <Label>{language === 'es' ? 'Método de Contacto' : 'Contact Method'}</Label>
            <RadioGroup 
              value={settings.contact_preference} 
              onValueChange={(v) => setSettings(prev => ({ ...prev, contact_preference: v }))}
            >
              <div className="flex items-center space-x-3">
                <RadioGroupItem value="in_app" id="in_app_contact" />
                <Label htmlFor="in_app_contact">{language === 'es' ? 'Solo en la App' : 'In-App Only'}</Label>
              </div>
              <div className="flex items-center space-x-3">
                <RadioGroupItem value="email" id="email_contact" />
                <Label htmlFor="email_contact">{language === 'es' ? 'Email También' : 'Email Also'}</Label>
              </div>
              <div className="flex items-center space-x-3">
                <RadioGroupItem value="both" id="both_contact" />
                <Label htmlFor="both_contact">{language === 'es' ? 'Ambos' : 'Both'}</Label>
              </div>
            </RadioGroup>
          </div>
        </CardContent>
      </Card>

      {/* Activity Settings */}
      <Card>
        <CardHeader>
          <CardTitle>{language === 'es' ? 'Actividad' : 'Activity'}</CardTitle>
          <CardDescription>
            {language === 'es' 
              ? 'Controla tu huella en la plataforma'
              : 'Control your footprint on the platform'}
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center justify-between py-2">
            <div>
              <Label>{language === 'es' ? 'Mostrar Estado de Actividad' : 'Show Activity Status'}</Label>
              <p className="text-sm text-muted-foreground">
                {language === 'es' ? 'Otros pueden ver si estás en línea' : 'Others can see if you\'re online'}
              </p>
            </div>
            <Switch
              checked={settings.show_activity_status}
              onCheckedChange={(checked) => setSettings(prev => ({ ...prev, show_activity_status: checked }))}
            />
          </div>

          <div className="flex items-center justify-between py-2 border-t">
            <div>
              <Label>{language === 'es' ? 'Mostrar Vistas de Perfil' : 'Show Profile Views'}</Label>
              <p className="text-sm text-muted-foreground">
                {language === 'es' ? 'Recibir notificaciones de visitas' : 'Get notified of profile visits'}
              </p>
            </div>
            <Switch
              checked={settings.show_profile_views}
              onCheckedChange={(checked) => setSettings(prev => ({ ...prev, show_profile_views: checked }))}
            />
          </div>

          <div className="flex items-center justify-between py-2 border-t">
            <div>
              <Label>{language === 'es' ? 'Dejar Huellas de Vista' : 'Leave View Traces'}</Label>
              <p className="text-sm text-muted-foreground">
                {language === 'es' ? 'Otros saben cuando ves su perfil' : 'Others know when you view their profile'}
              </p>
            </div>
            <Switch
              checked={settings.leave_view_traces}
              onCheckedChange={(checked) => setSettings(prev => ({ ...prev, leave_view_traces: checked }))}
            />
          </div>
        </CardContent>
      </Card>

      {/* Save Button */}
      <Button onClick={handleSave} disabled={saving} className="w-full" data-testid="save-btn">
        {saving ? (
          <Loader2 className="mr-2 h-4 w-4 animate-spin" />
        ) : (
          <Save className="mr-2 h-4 w-4" />
        )}
        {language === 'es' ? 'Guardar Cambios' : 'Save Changes'}
      </Button>

      {/* Safety Section */}
      <Card className="border-destructive/30">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-destructive">
            <AlertTriangle className="h-5 w-5" />
            {language === 'es' ? 'Zona de Seguridad' : 'Safety Zone'}
          </CardTitle>
          <CardDescription>
            {language === 'es' 
              ? 'Acciones de emergencia para proteger tu privacidad'
              : 'Emergency actions to protect your privacy'}
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center justify-between p-4 rounded-lg bg-destructive/5 border border-destructive/20">
            <div>
              <h4 className="font-medium flex items-center gap-2">
                <Power className="h-4 w-4" />
                {language === 'es' ? 'Modo Pánico' : 'Panic Mode'}
              </h4>
              <p className="text-sm text-muted-foreground">
                {language === 'es' 
                  ? 'Oculta tu perfil instantáneamente. Puedes reactivarlo después.'
                  : 'Instantly hide your profile. You can reactivate it later.'}
              </p>
            </div>
            <Button 
              variant="destructive" 
              size="sm"
              onClick={() => setPanicDialog(true)}
              data-testid="panic-btn"
            >
              {language === 'es' ? 'Activar' : 'Activate'}
            </Button>
          </div>

          <div className="flex items-center justify-between p-4 rounded-lg bg-destructive/5 border border-destructive/20">
            <div>
              <h4 className="font-medium flex items-center gap-2">
                <Trash2 className="h-4 w-4" />
                {language === 'es' ? 'Eliminar Perfil Comunitario' : 'Delete Community Profile'}
              </h4>
              <p className="text-sm text-muted-foreground">
                {language === 'es' 
                  ? 'Elimina permanentemente tu perfil y todos los datos asociados.'
                  : 'Permanently delete your profile and all associated data.'}
              </p>
            </div>
            <Button 
              variant="outline" 
              size="sm"
              className="text-destructive border-destructive hover:bg-destructive hover:text-destructive-foreground"
              onClick={() => setDeleteDialog(true)}
              data-testid="delete-btn"
            >
              {language === 'es' ? 'Eliminar' : 'Delete'}
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Panic Mode Dialog */}
      <AlertDialog open={panicDialog} onOpenChange={setPanicDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-destructive" />
              {language === 'es' ? 'Activar Modo Pánico' : 'Activate Panic Mode'}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {language === 'es' 
                ? 'Esto ocultará tu perfil inmediatamente. No serás visible en búsquedas y no recibirás mensajes. Puedes reactivar tu perfil más tarde desde esta página.'
                : 'This will hide your profile immediately. You won\'t be visible in searches and won\'t receive messages. You can reactivate your profile later from this page.'}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{language === 'es' ? 'Cancelar' : 'Cancel'}</AlertDialogCancel>
            <AlertDialogAction 
              onClick={handlePanicMode}
              disabled={panicLoading}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {panicLoading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
              {language === 'es' ? 'Activar Modo Pánico' : 'Activate Panic Mode'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Delete Profile Dialog */}
      <AlertDialog open={deleteDialog} onOpenChange={setDeleteDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <Trash2 className="h-5 w-5 text-destructive" />
              {language === 'es' ? '¿Eliminar Perfil Comunitario?' : 'Delete Community Profile?'}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {language === 'es' 
                ? 'Esta acción es permanente y no se puede deshacer. Se eliminarán tu perfil, conexiones, mensajes y membresías de grupo. Tu cuenta principal de PoliBot no se verá afectada.'
                : 'This action is permanent and cannot be undone. Your profile, connections, messages, and group memberships will be deleted. Your main PoliBot account will not be affected.'}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{language === 'es' ? 'Cancelar' : 'Cancel'}</AlertDialogCancel>
            <AlertDialogAction 
              onClick={handleDeleteProfile}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {language === 'es' ? 'Eliminar Permanentemente' : 'Delete Permanently'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default Privacy;
